import MessageDisplay from '@/components/MessageDisplay';
import { RectShape } from 'react-placeholder/lib/placeholders';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  image?: string;
  imageId?: string;
  previewImage?: string;
  isGeneratingImage?: boolean;
  imageGenerationStartTime?: number;
  model?: string;
  userPrompt?: string;
  promptId?: string;
  rating?: 'thumbs_up' | 'thumbs_down' | null;
  error?: string;
  hasError?: boolean;
}

interface MessageListProps {
  messages: Message[];
  sessionTitle: string;
  sessionId?: string;
  onRegenerate?: (modelId: string, prompt: string, promptId?: string, modelObject?: any) => void;
  isStreaming?: boolean;
  isCreatingSession?: boolean;
  isLoading?: boolean;
}

const MessagePlaceholder = () => (
  <div className="space-y-6 animate-pulse">
    <div className="flex justify-end">
      <div className="max-w-[70%]">
        <RectShape
          className="bg-gray-200 dark:bg-gray-700"
          style={{ width: 300, height: 60, borderRadius: 8 }}
        />
      </div>
    </div>
    <div className="flex justify-start">
      <div className="w-[70%] space-y-3">
        <RectShape
          className="bg-gray-200 dark:bg-gray-800"
          style={{height: 200, borderRadius: 8 }}
        />
      </div>
    </div>
  </div>
);

export default function MessageList({
  messages,
  sessionTitle,
  sessionId,
  onRegenerate,
  isStreaming,
  isCreatingSession,
  isLoading = false,
}: MessageListProps) {
  // Find all messages with images for navigation
  const imagesMessages = messages.filter(
    (m) => m.role === 'assistant' && (m.imageId || m.image)
  );

  if (isLoading) {
    return <MessagePlaceholder />;
  }

  return (
    <>
      {/* Render messages */}
      {messages.map((message) => {
        const currentImageIndex = imagesMessages.findIndex((m) => m.id === message.id);

        return (
          <MessageDisplay
            key={message.id}
            sessionTitle={sessionTitle}
            message={message}
            sessionId={sessionId}
            onRegenerate={(modelId, prompt, promptId, modelObject) => {
              if (onRegenerate) {
                onRegenerate(prompt, modelId, promptId, modelObject);
              }
            }}
            allImageMessages={imagesMessages}
            currentImageIndex={currentImageIndex >= 0 ? currentImageIndex : 0}
          />
        );
      })}

      {/* Loading dots as a message */}
      {(isStreaming || isCreatingSession) &&
        !messages.some((m) => m.isGeneratingImage) && (
          <div className="flex justify-start mb-6">
            <div className="flex gap-1">
              <div className="w-3 h-3 bg-[#00FFBF] rounded-full animate-bounce" />
              <div
                className="w-3 h-3 bg-[#FF0090] rounded-full animate-bounce"
                style={{ animationDelay: '0.1s' }}
              />
              <div
                className="w-3 h-3 bg-[#FCAF28] rounded-full animate-bounce"
                style={{ animationDelay: '0.2s' }}
              />
            </div>
          </div>
        )}
    </>
  );
}